import styled from "styled-components";
import type { IFontProps } from "@/typings/Props";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { media } from "@/styles/media";
import { theme } from "@/styles/theme";

interface ProductType {
  id: number;
  imgSrc: string;
  title: string;
  description: string;
  link: string;
}

export default function Products() {
  const { t } = useTranslation();
  const products: ProductType[] = [
    {
      id: 1,
      imgSrc: "main/products/machine_05.png",
      title: t("main.product.product-1.title"),
      description: t("main.product.product-1.desc"),
      link: "/products/0",
    },
    {
      id: 2,
      imgSrc: "main/products/BWA-removebg-preview-2.png",
      title: t("main.product.product-2.title"),
      description: t("main.product.product-2.desc"),
      link: "/products/1",
    },
    {
      id: 3,
      imgSrc: "main/products/Blood_Pressure-removebg-preview-2.png",
      title: t("main.product.product-3.title"),
      description: t("main.product.product-3.desc"),
      link: "/products/2",
    },
    {
      id: 4,
      imgSrc: "main/products/Stadiometer-removebg-preview-2.png",
      title: t("main.product.product-4.title"),
      description: t("main.product.product-4.desc"),
      link: "/products/3",
    },
  ];
  return (
    <ContentWrapper>
      <TitleWrapper>
        <Title data-aos="fade-up" data-aos-easing="ease-out" data-aos-delay="100" data-aos-duration="500">
          {t("main.product.title")}
        </Title>
        <Button href="/all-products">{t("view-all-products")}</Button>
      </TitleWrapper>
      <ProductItems>
        {products.map((product) => {
          return (
            <Item key={product.id}>
              <Link href={product.link}>
                <ItemImg src={product.imgSrc} alt={product.title} />
                <InnerText>
                  <P $fontSize="2rem" $fontWeight="600">
                    {product.title}
                  </P>
                  <P $fontSize="0.9rem" $fontColor={theme.colors.text.default} className="title">
                    {product.description}
                  </P>
                </InnerText>
                <Icon src="/icons/arrow-right-solid.svg" alt="arrow_icon" />
              </Link>
            </Item>
          );
        })}
      </ProductItems>
    </ContentWrapper>
  );
}

const ContentWrapper = styled.div`
  width: 100%;
  height: 700px;
  position: relative;

  ${media.tablet`
    height: auto;
  `}
`;
const TitleWrapper = styled.div`
  display: flex;
  align-items: center;

  ${media.mobile`
    flex-wrap: wrap;
  `}
`;
const Title = styled.p`
  width: 100%;
  padding: 40px;
  font-size: 2.7rem;
  font-weight: ${theme.fontWeights.bold};
  display: flex;
  justify-content: space-between;
  color: ${theme.colors.primary};
  ${media.phablet`
    font-size: 2rem;
  `}
  ${media.mini`
    font-size: 1.5rem;
  `}
`;

const P = styled.p<IFontProps>`
  line-height: 130%;
  padding-top: 3px;
  text-align: left;
  font-size: ${(props) => (props.$fontSize ? props.$fontSize : "1rem")};
  font-weight: ${(props) => (props.$fontWeight ? props.$fontWeight : "400")};
  color: ${(props) => props.$fontColor};
`;

const Button = styled(Link)`
  width: 200px;
  height: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 50px;
  padding: 10px;
  border-radius: 20px;
  background-color: black;
  color: ${theme.colors.text.white};
  cursor: pointer;
  border: 1px solid ${theme.colors.border.black};
  transition: 0.3s ease-out;
  ${media.mobile`  
    margin: auto;
    margin-bottom: 15px;
  `}
  &:hover {
    background-color: ${theme.colors.background.white};
    color: ${theme.colors.text.default};
    transition: 0.3s ease-out;
  }
`;

// Products
const ProductItems = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  ${media.tablet`
    flex-wrap: wrap;
    flex-direction: row;
    justify-content: space-evenly;
  `}
  ${media.mobile`
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
  `}
`;

// Product Item
const ItemImg = styled.img`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: auto;
  transition-duration: 0.3s;
`;

const InnerText = styled.div`
  position: absolute;
  top: 18%;
  left: 50%;
  width: 100%;
  padding: 0 30px;
  transform: translate(-50%, -50%);
  text-align: center;
  line-height: 150%;
`;

const Item = styled.div`
  position: relative;
  width: 100%;
  height: 547px;
  margin: 20px;
  background-color: white;
  border-radius: 20px;
  overflow: hidden;
  color: ${theme.colors.primary};
  &:hover {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.text.white};
    transition-duration: 0.3s;
    & a > ${InnerText} > .title {
      color: ${theme.colors.text.white};
    }
    & a > ${ItemImg} {
      transform: scale(1.2, 1.2);
      transition-duration: 0.3s;
    }
  }
  ${media.tablet`
    width: 48%;
    margin: 10px 0;
    flex-wrap: wrap;
  `}

  ${media.mobile`
    width: 95%;
  `}
`;

const Icon = styled.img`
  position: absolute;
  top: 30px;
  right: 20px;
  width: 35px;
  transform: rotate(-45deg);
`;
